// 約束ベース Focus / Pipeline の純粋ロジック（DEC-054 / COUNCIL-20260606-001）
//
// バックエンド（Cloud Functions）が議事録抽出時に書き込んだ Firestore データを
// 「読むだけ」で Focus（今日果たす約束）と Pipeline（案件俯瞰）を組み立てる。
// このモジュールは Firestore に一切依存しない純関数のみ（テスト容易性のため）。
//
// 参照する Firestore フィールド（camelCase・抽出由来は Admin SDK のみ書込み）:
//   issue: promiseType / ballOwner('us'|'counterparty') / audience('customer'|'internal')
//          / evidence(逐語) / snoozeUntil / dueDate / status / createdAt / sourceMinutesIds
//   meeting: category('sales'|'internal') / companyIds / recordedAt
//   project.deal: ball / hot / hotEvidence / confidenceBand / customerIssue
//
// DEC-009 厳守: 受注%・的中率・点数ゲージは一切出さない。スコアは内部の並び順専用。

// ─────────────────────────────────────────────────────────────
// 約束タイプ レジストリ（固定5＋その他。分岐ハードコードせずレジストリ参照）
// 未知 ID は other 相当にフォールバックするが、元 ID は保持して在庫から落とさない（必達7）。
// ─────────────────────────────────────────────────────────────
export type PillKind = 'amber' | 'teal' | 'info' | 'gray' | 'danger' | 'success';

export interface PromiseTypeDef {
  id: string;
  label: string; // 在庫チップ等の短ラベル
  cardLabel: string; // 約束カード用の長ラベル
  emoji: string;
  weight: number; // スコア用 typeWeight（📄5/📎4/📅3/🔧2/他1）
  pill: PillKind;
  core: boolean; // 在庫サマリーで常時表示する固定タイプか
}

export const PROMISE_TYPES: PromiseTypeDef[] = [
  { id: 'quote', label: '見積', cardLabel: '見積提出', emoji: '📄', weight: 5, pill: 'amber', core: true },
  { id: 'doc', label: '資料', cardLabel: '資料・データ送付', emoji: '📎', weight: 4, pill: 'teal', core: true },
  { id: 'appt', label: 'アポ', cardLabel: 'アポ・日程調整', emoji: '📅', weight: 3, pill: 'info', core: true },
  { id: 'support', label: '対応', cardLabel: '検証・サポート', emoji: '🔧', weight: 2, pill: 'gray', core: true },
  { id: 'other', label: 'その他', cardLabel: 'その他', emoji: '•', weight: 1, pill: 'gray', core: false },
];

const TYPE_MAP: Record<string, PromiseTypeDef> = Object.fromEntries(
  PROMISE_TYPES.map((t) => [t.id, t])
);

const OTHER_TYPE = TYPE_MAP['other'];

// 未知 ID は other 定義にフォールバック（在庫から落とさない）。元 ID は呼び出し側で別途保持する。
export function resolvePromiseType(id?: string | null): PromiseTypeDef {
  if (id && TYPE_MAP[id]) return TYPE_MAP[id];
  return OTHER_TYPE;
}

export const UNRESOLVED_STATUSES = ['open', 'in_progress'];

// ─────────────────────────────────────────────────────────────
// 日付ユーティリティ
// ─────────────────────────────────────────────────────────────
// Firestore Timestamp / Date / 文字列 / null を Date|null に正規化する。
// 注: backend は snoozeUntil を '' または 'YYYY-MM-DD' 文字列で書く場合があり
//     （クライアント側の [明日でいい] は Timestamp で書く）、両方をここで吸収する。
//     '' → Invalid Date → null（＝非スヌーズ）。
export function toDate(ts: any): Date | null {
  if (ts == null) return null;
  if (typeof ts.toDate === 'function') return ts.toDate(); // Firestore Timestamp
  if (ts instanceof Date) return isNaN(ts.getTime()) ? null : ts;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

// a から b までの経過日数（暦日ベース・負値は 0 に丸めない＝超過日数判定に使う）。
export function diffDays(from: Date, to: Date): number {
  const MS = 24 * 60 * 60 * 1000;
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((b - a) / MS);
}

export function formatMonthDay(d: Date | null): string {
  if (!d) return '';
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// ─────────────────────────────────────────────────────────────
// 入力型（Firestore 生データに対応）
// ─────────────────────────────────────────────────────────────
export interface RawIssue {
  id: string;
  projectId: string;
  title?: string;
  status?: string;
  promiseType?: string;
  ballOwner?: string; // 'us' | 'counterparty'
  audience?: string; // 'customer' | 'internal'
  evidence?: string;
  snoozeUntil?: any;
  dueDate?: any;
  createdAt?: any;
  sourceMinutesIds?: string[];
  // S-058-3: 紐づく決定の会議内ID（D1..・任意・多対1）。Focus は約束ベースのままで本フィールドは未使用だが、
  // 同じ issue 形を読む経路で型を揃えておく（欠損は空＝「その他」扱い）。
  decisionRef?: string;
}

export interface ProjectCtx {
  id: string;
  name: string;
  companyName?: string; // 解決済み主担当社名（無ければ相手未特定）
  // 案件 deal（抽出由来・読取専用）
  deal?: {
    ball?: string; // 'us' | 'counterparty' | 'done'
    hot?: boolean;
    hotEvidence?: string;
    confidenceBand?: string; // 'high' | 'mid' | 'low'
    customerIssue?: string;
  };
  meetingDates?: Record<string, Date | null>; // meetingId -> recordedAt
  meetingTitles?: Record<string, string>; // meetingId -> タイトル（議事録への遷移ラベル用）
}

// ─────────────────────────────────────────────────────────────
// 約束（enriched issue）
// ─────────────────────────────────────────────────────────────
export interface FocusPromise {
  id: string;
  projectId: string;
  projectName: string;
  companyName?: string;
  title: string;
  type: PromiseTypeDef;
  rawType: string; // 元 promiseType ID（未知含む）
  evidence?: string;
  audience: 'customer' | 'internal';
  ballOwner: 'us' | 'counterparty';
  dueDate: Date | null;
  overdue: boolean;
  meetingDate: Date | null; // 約束した会議の日付
  sourceMeetingId: string | null; // 約束の出典会議 ID（議事録への遷移用・sourceMinutesIds[0]）
  sourceMeetingTitle: string | null; // 出典会議のタイトル（遷移先ヘッダ用・無ければ null）
  ageDays: number; // 経過日数（会議日 or createdAt 起点）
  estimated: boolean; // 会議日が取れず createdAt から推定したか
  snoozeUntil: Date | null;
  hot: boolean; // 所属案件がホットか（カード右肩 🔥 用）
  confidenceBand?: string;
  score: number; // 内部スコア（UI 非表示）
}

// バックエンドの安全側デフォルトに合わせる: 明示的に 'customer' のときだけ顧客向け、
// 欠損・空・不明はすべて 'internal' に倒す（audience フィールド以前の旧 issue が
// 顧客向け Focus を汚染しないように）。backend _persistExtractedIssues も同じ既定。
function normAudience(v?: string): 'customer' | 'internal' {
  return v === 'customer' ? 'customer' : 'internal';
}
function normBall(v?: string): 'us' | 'counterparty' {
  return v === 'counterparty' ? 'counterparty' : 'us';
}

// 内部スコア = typeWeight × deadlineFactor × ageFactor × dealHeat（DEC-009: UI 非表示）
export function computeScore(p: {
  weight: number;
  dueDate: Date | null;
  overdue: boolean;
  ageDays: number;
  hot: boolean;
  confidenceBand?: string;
}): number {
  const deadlineFactor = p.overdue ? 2.6 : p.dueDate ? 2.0 : 1.0;
  const ageFactor = 1 + Math.min(Math.max(p.ageDays, 0), 30) / 15;
  const dealHeat = p.hot ? 1.5 : p.confidenceBand === 'high' ? 1.3 : 1.0;
  return p.weight * deadlineFactor * ageFactor * dealHeat;
}

export function buildFocusPromise(issue: RawIssue, ctx: ProjectCtx, now: Date): FocusPromise {
  const type = resolvePromiseType(issue.promiseType);
  const dueDate = toDate(issue.dueDate);
  const overdue = dueDate ? diffDays(now, dueDate) < 0 : false;
  const srcId = issue.sourceMinutesIds && issue.sourceMinutesIds.length > 0 ? issue.sourceMinutesIds[0] : undefined;
  const meetingDate = srcId && ctx.meetingDates ? ctx.meetingDates[srcId] ?? null : null;
  const sourceMeetingTitle = srcId && ctx.meetingTitles ? ctx.meetingTitles[srcId] ?? null : null;
  const createdAt = toDate(issue.createdAt);
  const baseDate = meetingDate ?? createdAt;
  const ageDays = baseDate ? Math.max(0, diffDays(baseDate, now)) : 0;
  const estimated = meetingDate == null;
  const hot = ctx.deal?.hot === true;
  const confidenceBand = ctx.deal?.confidenceBand;
  const score = computeScore({ weight: type.weight, dueDate, overdue, ageDays, hot, confidenceBand });

  return {
    id: issue.id,
    projectId: ctx.id,
    projectName: ctx.name,
    companyName: ctx.companyName,
    title: issue.title || '無題の約束',
    type,
    rawType: issue.promiseType || 'other',
    evidence: issue.evidence || undefined,
    audience: normAudience(issue.audience),
    ballOwner: normBall(issue.ballOwner),
    dueDate,
    overdue,
    meetingDate,
    sourceMeetingId: srcId ?? null,
    sourceMeetingTitle,
    ageDays,
    estimated,
    snoozeUntil: toDate(issue.snoozeUntil),
    hot,
    confidenceBand,
    score,
  };
}

// 未解決か（resolved/closed を除外）。
export function isUnresolved(issue: { status?: string }): boolean {
  return UNRESOLVED_STATUSES.includes(issue.status ?? 'open');
}

// 翌朝以降にスヌーズされているか（snoozeUntil が「今日の終わり」より先）。
export function isSnoozed(p: FocusPromise, now: Date): boolean {
  if (!p.snoozeUntil) return false;
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return p.snoozeUntil.getTime() > endOfToday.getTime();
}

// 商談系（顧客向け）約束のうち、未スヌーズのものをスコア順に並べる。
export function rankCustomerPromises(promises: FocusPromise[], now: Date): FocusPromise[] {
  return promises
    .filter((p) => p.audience === 'customer' && !isSnoozed(p, now))
    .sort((a, b) => b.score - a.score);
}

// 今日のトップ3（相手未特定はトップ3に出さない）。同点・同日締切のみ最大5件まで伸ばす。
export function pickTopPromises(ranked: FocusPromise[]): { top: FocusPromise[]; rest: FocusPromise[] } {
  const eligible = ranked.filter((p) => !!p.companyName);
  const ineligible = ranked.filter((p) => !p.companyName);
  let cut = Math.min(3, eligible.length);
  // 3 件目と 4 件目が同点かつ同じ締切日なら最大 5 件まで含める。
  while (cut < eligible.length && cut < 5) {
    const prev = eligible[cut - 1];
    const cur = eligible[cut];
    const sameScore = Math.abs(prev.score - cur.score) < 1e-9;
    const sameDue = (prev.dueDate?.getTime() ?? -1) === (cur.dueDate?.getTime() ?? -2);
    if (sameScore && sameDue) cut++;
    else break;
  }
  const top = eligible.slice(0, cut);
  const rest = [...eligible.slice(cut), ...ineligible];
  return { top, rest };
}

// 在庫サマリー: タイプ別件数（商談系・未スヌーズ）。
export interface InventoryChip {
  type: PromiseTypeDef;
  count: number;
}
export function inventory(promises: FocusPromise[], now: Date): {
  total: number;
  chips: InventoryChip[];
  unidentified: number; // 相手未特定件数
} {
  const customer = promises.filter((p) => p.audience === 'customer' && !isSnoozed(p, now));
  const counts: Record<string, number> = {};
  customer.forEach((p) => {
    // 未知 ID は other チップに合算（在庫から落とさない）。
    const key = TYPE_MAP[p.rawType] ? p.rawType : 'other';
    counts[key] = (counts[key] || 0) + 1;
  });
  const chips: InventoryChip[] = PROMISE_TYPES
    .filter((t) => t.core || (counts[t.id] || 0) > 0) // 固定4は常時、その他は>0のとき
    .map((t) => ({ type: t, count: counts[t.id] || 0 }));
  const unidentified = customer.filter((p) => !p.companyName).length;
  return { total: customer.length, chips, unidentified };
}

// 社内タスク（audience=internal・未スヌーズ）。
export function internalPromises(promises: FocusPromise[], now: Date): FocusPromise[] {
  return promises
    .filter((p) => p.audience === 'internal' && !isSnoozed(p, now))
    .sort((a, b) => b.score - a.score);
}

// ─────────────────────────────────────────────────────────────
// Pipeline
// ─────────────────────────────────────────────────────────────

// フォロー漏れ判定: 自社ボール（us）の顧客向け約束で、期限超過 or 14日以上停滞。
export function isLeak(p: FocusPromise): boolean {
  if (p.audience !== 'customer') return false;
  if (p.ballOwner !== 'us') return false;
  return p.overdue || p.ageDays >= 14;
}

export function leakPromises(promises: FocusPromise[], now: Date): FocusPromise[] {
  return promises
    .filter((p) => !isSnoozed(p, now) && isLeak(p))
    .sort((a, b) => {
      // 期限超過を先頭、その後は経過日数の多い順。
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
      return b.ageDays - a.ageDays;
    });
}

// フォロー漏れチップの meta 文言（「期限超過」or「N日停滞 / N日経過」）。
export function leakMeta(p: FocusPromise): string {
  if (p.overdue) return '期限超過';
  return `${p.ageDays}日経過`;
}

export interface DealCard {
  projectId: string;
  projectName: string;
  companyName?: string;
  displayName: string; // 「会社 ・ 案件名」or 案件名
  customerIssue?: string;
  confidenceBand?: 'high' | 'mid' | 'low' | string;
  hot: boolean;
  hotEvidence?: string;
  ball: 'us' | 'counterparty' | 'done' | string;
  promiseCount: number; // 未解決の顧客向け約束数
  leakCount: number;
  leadPromise?: FocusPromise; // 代表約束（最高スコア）
  hasDeal: boolean; // deal 抽出データを持つか
}

const BAND_RANK: Record<string, number> = { high: 3, mid: 2, low: 1 };

export function buildDeals(projects: ProjectCtx[], promisesByProject: Record<string, FocusPromise[]>, now: Date): DealCard[] {
  const deals: DealCard[] = projects.map((proj) => {
    const ps = (promisesByProject[proj.id] || []).filter(
      (p) => p.audience === 'customer' && !isSnoozed(p, now)
    );
    const lead = [...ps].sort((a, b) => b.score - a.score)[0];
    const leakCount = ps.filter((p) => isLeak(p)).length;
    const deal = proj.deal;
    const displayName = proj.companyName ? `${proj.companyName} ・ ${proj.name}` : proj.name;
    return {
      projectId: proj.id,
      projectName: proj.name,
      companyName: proj.companyName,
      displayName,
      customerIssue: deal?.customerIssue || undefined,
      confidenceBand: deal?.confidenceBand,
      hot: deal?.hot === true,
      hotEvidence: deal?.hotEvidence || undefined,
      ball: (deal?.ball as any) || 'us',
      promiseCount: ps.length,
      leakCount,
      leadPromise: lead,
      hasDeal: !!deal && (deal.confidenceBand != null || deal.customerIssue != null || deal.ball != null || deal.hot === true),
    };
  });

  // 手応え（high>mid>low）→ ホット → フォロー漏れ多い順。
  return deals.sort((a, b) => {
    const br = (BAND_RANK[b.confidenceBand ?? ''] ?? 0) - (BAND_RANK[a.confidenceBand ?? ''] ?? 0);
    if (br !== 0) return br;
    if (a.hot !== b.hot) return a.hot ? -1 : 1;
    return b.leakCount - a.leakCount;
  });
}

// 手応えバンドの表示語＋ピル種別（DEC-009: 数値なし・語のみ）。
export function confidenceLabel(band?: string): { label: string; pill: PillKind } | null {
  switch (band) {
    case 'high':
      return { label: '手応え 高', pill: 'amber' };
    case 'mid':
      return { label: '手応え 中', pill: 'teal' };
    case 'low':
      return { label: '手応え 低', pill: 'gray' };
    default:
      return null;
  }
}

export function ballLabel(ball?: string): { label: string; pill: PillKind } | null {
  switch (ball) {
    case 'us':
      return { label: '自社ボール', pill: 'amber' };
    case 'counterparty':
      return { label: '相手ボール', pill: 'gray' };
    case 'done':
      return { label: '完了', pill: 'success' };
    default:
      return null;
  }
}
